/**
 * Updates recently viewed products when Cookiebot preferences consent is granted.
 */
export class RecentlyViewed {
  /** @static @constant {string} The key used to store the viewed products in local storage */
  static #STORAGE_KEY = 'viewedProducts';
  /** @static @constant {number} The maximum number of products to store */
  static #MAX_PRODUCTS = 4;
  static #consentEvents = ['CookiebotOnConsentReady', 'CookiebotOnAccept', 'CookiebotOnDecline'];
  static #consentListenerInitialized = false;

  static #hasPreferencesConsent() {
    return window.Cookiebot?.consent?.preferences === true;
  }

  static initializeConsentListener() {
    if (this.#consentListenerInitialized) return;

    this.#consentListenerInitialized = true;

    const clearWithoutPreferencesConsent = () => {
      if (!this.#hasPreferencesConsent()) this.clearProducts();
    };

    this.#consentEvents.forEach((eventName) => {
      window.addEventListener(eventName, clearWithoutPreferencesConsent);
    });

    clearWithoutPreferencesConsent();
  }

  /**
   * Adds a product to the recently viewed products list.
   * @param {string} productId - The ID of the product to add.
   */
  static addProduct(productId) {
    if (!this.#hasPreferencesConsent()) {
      this.clearProducts();
      return;
    }

    let viewedProducts = this.getProducts();

    viewedProducts = viewedProducts.filter((/** @type {string} */ id) => id !== productId);
    viewedProducts.unshift(productId);
    viewedProducts = viewedProducts.slice(0, this.#MAX_PRODUCTS);

    localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(viewedProducts));
  }

  static clearProducts() {
    localStorage.removeItem(this.#STORAGE_KEY);
  }

  /**
   * Retrieves the list of recently viewed products from local storage.
   * @returns {string[]} The list of viewed products.
   */
  static getProducts() {
    if (!this.#hasPreferencesConsent()) {
      this.clearProducts();
      return [];
    }

    return JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');
  }
}

RecentlyViewed.initializeConsentListener();
